import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Search, MapPin, Star, Filter, Heart, Phone, Mail,
  Users, Dumbbell, ChevronRight, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import NearbyStudiosWidget from '../components/NearbyStudiosWidget';
import { formatActivity } from '../lib/formatActivity';

const categories = [
  { value: 'all', label: 'All Studios' },
  { value: 'yoga', label: 'Yoga' },
  { value: 'pilates', label: 'Pilates' },
  { value: 'crossfit', label: 'CrossFit' },
  { value: 'gym', label: 'Gym' },
  { value: 'fitness', label: 'Fitness' },
];

const studioImages = [
  'https://images.unsplash.com/photo-1767611114990-6aa3b22cde42?w=600&q=80',
  'https://images.unsplash.com/photo-1771270786606-f5a0e57db762?w=600&q=80',
  'https://images.unsplash.com/photo-1758875569284-c57e79ef75e0?w=600&q=80',
];

export const StudiosListPage = () => {
  const { user, toggleFavouriteStudio, api, isAuthenticated } = useAuth();
  const [studios, setStudios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const fetchStudios = useCallback(async () => {
    try {
      let url = '/studios?';
      if (searchQuery) url += `search=${searchQuery}&`;
      if (selectedCategory !== 'all') url += `category=${selectedCategory}`;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${url}`);
      const data = await response.json();
      
      // Fetch ratings and member counts for each studio
      const studiosWithStats = await Promise.all(data.map(async (studio) => {
        try {
          // Fetch reviews for rating
          const reviewsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/studio/${studio.id}`);
          const reviewsData = reviewsRes.ok ? await reviewsRes.json() : { reviews: [], average_rating: null, total_reviews: 0 };
          const reviews = reviewsData.reviews || [];
          
          // Use average rating from API response
          const avgRating = reviewsData.average_rating ? reviewsData.average_rating.toFixed(1) : null;
          
          // Fetch member count (unique clients)
          const clientsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/studios/${studio.id}/client-count`);
          const clientData = clientsRes.ok ? await clientsRes.json() : { count: 0 };
          
          return {
            ...studio,
            average_rating: avgRating,
            review_count: reviewsData.total_reviews || 0,
            member_count: clientData.count || 0
          };
        } catch (e) {
          return { ...studio, average_rating: null, review_count: 0, member_count: 0 };
        }
      }));
      
      // Sort favourites to top if user is logged in
      const favs = user?.favourite_studios || user?.favorite_studios || [];
      if (favs.length > 0) {
        studiosWithStats.sort((a, b) => {
          const aFav = favs.includes(a.id);
          const bFav = favs.includes(b.id);
          if (aFav && !bFav) return -1;
          if (!aFav && bFav) return 1;
          return 0;
        });
      }
      
      setStudios(studiosWithStats);
    } catch (e) {
      console.error('Failed to fetch studios:', e);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCategory, user]);

  useEffect(() => {
    fetchStudios();
  }, [fetchStudios]);

  const getMemberCountLabel = (count) => {
    if (count >= 500) return '500+ Members';
    if (count >= 400) return '400+ Members';
    if (count >= 300) return '300+ Members';
    if (count >= 200) return '200+ Members';
    if (count >= 100) return '100+ Members';
    if (count >= 50) return '50+ Members';
    return '< 50 Members';
  };

  const getFullAddress = (studio) => {
    const parts = [
      studio.address,
      studio.address_line_2,
      studio.city,
      studio.county,
      studio.eircode
    ].filter(Boolean);
    return parts.join(', ') || 'View location details';
  };

  const handleFavourite = async (e, studioId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      toast.error('Please login to save favourites');
      return;
    }
    try {
      await toggleFavouriteStudio(studioId);
      toast.success('Favourites updated');
    } catch (e) {
      toast.error('Failed to update favourites');
    }
  };

  const isFavourite = (studioId) => {
    const favs = user?.favourite_studios || user?.favorite_studios || [];
    return favs.includes(studioId);
  };

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <div className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Find Your Studio</h1>
          <p className="text-xl text-slate-300 mb-8">
            Discover the best fitness studios near you
          </p>
          
          {/* Search Bar */}
          <div className="flex flex-col sm:flex-row gap-4 max-w-3xl">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search studios by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-14 bg-white text-slate-900 border-0"
                data-testid="studio-search-input"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full sm:w-48 h-14 bg-white text-slate-900 border-0" data-testid="category-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Studios Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Iter-46an: nearby studios widget — only renders when geolocation
            is supported; user must opt in via "Use my location". */}
        <div className="mb-8">
          <NearbyStudiosWidget />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : studios.length > 0 ? (
          <>
            {user?.favourite_studios?.length > 0 && (
              <div className="mb-4 flex items-center gap-2">
                <Heart className="w-4 h-4 text-rose-500 fill-rose-500" />
                <span className="text-sm text-muted-foreground">Your favourite studios appear first</span>
              </div>
            )}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {studios.map((studio, index) => (
                <Link key={studio.id} to={`/studio/${studio.slug}`}>
                  <Card 
                    className="group overflow-hidden hover:shadow-xl transition-all duration-300 h-full"
                    data-testid={`studio-card-${index}`}
                  >
                    <div className="aspect-video relative overflow-hidden">
                      <img 
                        src={studio.cover_image_url || studioImages[index % studioImages.length]}
                        alt={studio.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <button
                        onClick={(e) => handleFavourite(e, studio.id)}
                        className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors"
                        data-testid={`favourite-btn-${index}`}
                      >
                        <Heart className={`w-5 h-5 ${isFavourite(studio.id) ? 'text-rose-500 fill-rose-500' : 'text-slate-600'}`} />
                      </button>
                      <div className="absolute bottom-3 left-3 flex gap-2">
                        <Badge className="bg-white/90 text-slate-900">{formatActivity(studio.category) || 'Fitness'}</Badge>
                        {isFavourite(studio.id) && (
                          <Badge className="bg-rose-500 text-white">Favourite</Badge>
                        )}
                      </div>
                    </div>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span className="truncate">{studio.name}</span>
                        {studio.average_rating && (
                          <div className="flex items-center gap-1 text-amber-500">
                            <Star className="w-4 h-4 fill-current" />
                            <span className="text-sm">{studio.average_rating}</span>
                            <span className="text-xs text-muted-foreground">({studio.review_count})</span>
                          </div>
                        )}
                      </CardTitle>
                      <CardDescription className="space-y-1">
                        <div className="flex items-start gap-1">
                          <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{getFullAddress(studio)}</span>
                        </div>
                        {(studio.phone || studio.email) && (
                          <div className="flex items-center gap-3 text-xs">
                            {studio.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {studio.phone}
                              </span>
                            )}
                            {studio.email && (
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {studio.email}
                              </span>
                            )}
                          </div>
                        )}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                        {studio.description || 'A premier fitness studio offering world-class training and facilities.'}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {getMemberCountLabel(studio.member_count)}
                          </span>
                        </div>
                        <ChevronRight className="w-5 h-5 text-primary group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-20">
            <Dumbbell className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Studios Found</h3>
            <p className="text-muted-foreground mb-6">
              {searchQuery ? `No results for "${searchQuery}"` : 'Be the first to create a studio!'}
            </p>
            <Link to="/register">
              <Button data-testid="create-studio-btn">Create Your Studio</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};
